import { CUSTOMER_LEVEL, ISuccessApiResponse } from '@wyz/types';
import {
  callApi,
  useOnSuccessQuery,
  useQuery,
  UseQueryResult,
} from '@wyz/utils';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CommonQueryKeys } from '~/common/api';
import { useApplicationContext, useConfig } from '~/common/context';
import { applyTheme, chatDisplay, initOmniCanal } from '~/common/helpers';
import { ICustomerOption, IUserCustomerPreview } from '~/common/types';
import { CustomerSelectorHelpers } from '~/connect/helpers';
import { BaseCustomerSelector } from '~/purchase/components';
import { usePurchaseContext } from '~/purchase/context';

interface SharedCustomerSelectorProps {
  customerQuery: UseQueryResult<
    ISuccessApiResponse<IUserCustomerPreview[]>,
    unknown
  >;
  onChange: (selectedOption: ICustomerOption | null) => void;
}

function SharedCustomerSelector({
  customerQuery,
  onChange,
}: SharedCustomerSelectorProps) {
  const [searchParams] = useSearchParams();
  const { state, handlers } = usePurchaseContext();
  const { application, auth } = useApplicationContext();
  const config = useConfig();
  const navigate = useNavigate();
  const call = callApi({});

  const chatTokenQuery = useQuery({
    queryKey: CommonQueryKeys.appContext('chat' + auth.user?.profile.sub),
    enabled:
      state.primaryCustomer?.level === CUSTOMER_LEVEL.N1 &&
      application.features.enabledChat &&
      Boolean(state.primaryCustomer.deliveryInformation?.country?.chatId),
    refetchOnMount: true,
    queryFn: () =>
      call<ISuccessApiResponse<string>>(
        `${config.apiUrl}/tenants/api/wtb/tenants/${config.tenantCode}/customers/${state.primaryCustomer?.code}/chat/token`,
      ),
  });

  useOnSuccessQuery(chatTokenQuery, (chatToken) => {
    const chatId = state.primaryCustomer?.deliveryInformation?.country?.chatId;
    if (application?.features.enabledChat && chatToken.data && chatId) {
      initOmniCanal(
        chatId,
        chatToken.data,
        application?.userTheme?.primaryColor || undefined,
      );
    }
  });

  useEffect(() => {
    if (state.primaryCustomer) {
      applyTheme(state.primaryCustomer.matchedTheme ?? application.userTheme);
    }
  }, [application.userTheme, state.primaryCustomer]);

  useEffect(() => {
    if (state.primaryCustomer?.level) {
      // @ts-expect-error required by usetiful
      window.usetifulTags = {
        // @ts-expect-error required by usetiful
        ...window.usetifulTags,
        customerType: state.primaryCustomer.level,
        mode:
          state.primaryCustomer.level === CUSTOMER_LEVEL.N1
            ? 'DISTRIBUTION'
            : 'REDISTRIBUTION',
      };
    }
  }, [state.primaryCustomer?.level]);

  useEffect(() => {
    if (application.features.enabledChat && state.primaryCustomer?.level) {
      chatDisplay(state.primaryCustomer?.level === CUSTOMER_LEVEL.N1);
    }
  }, [state.primaryCustomer?.level, application.features.enabledChat]);

  useOnSuccessQuery(customerQuery, (customersByUser) => {
    const customerFromQs = searchParams.get('customer');
    const fallbackCustomer =
      customersByUser.data.length === 1
        ? customersByUser.data.at(0)
        : undefined;
    const selected = customersByUser.data.find((customer) =>
      customerFromQs
        ? customer.id === customerFromQs
        : customer.id === state.selectedPrimaryCustomerId,
    );
    // user asked for a customer that does not exist
    if (customerFromQs && !selected) {
      void navigate('/');
      return;
    }
    if (!selected) {
      handlers.clearSelectedCustomers();
    }

    handlers.setPrimaryCustomerId(
      selected?.id || fallbackCustomer?.id || undefined,
    );
  });

  return (
    <BaseCustomerSelector
      key={state.selectedPrimaryCustomerId}
      isLoading={
        customerQuery.isProcessing || state.loading.isLoadingPrimaryCustomer
      }
      isDisabled={
        !customerQuery.isSuccess ||
        customerQuery.data?.data.length === 0 ||
        state.loading.isLoadingPrimaryCustomer
      }
      value={
        state.selectedPrimaryCustomerId && state.primaryCustomer
          ? CustomerSelectorHelpers.customerToOption({
              ...state.primaryCustomer,
              deliveryInformation: {
                city: state.primaryCustomer.deliveryInformation?.city ?? '',
                zipCode:
                  state.primaryCustomer.deliveryInformation?.zipCode ?? '',
                country: {
                  currencyId:
                    state.primaryCustomer.deliveryInformation?.country
                      ?.currencyId ?? 0,
                },
              },
            })
          : undefined
      }
      options={
        customerQuery.data?.data.map((item) =>
          CustomerSelectorHelpers.customerToOption(item),
        ) || []
      }
      onChange={onChange}
      data-cy={'customer'}
    />
  );
}

export default SharedCustomerSelector;
